import React, { useState, useContext, useEffect } from 'react';
import {Modal, Box, Typography, TextField, Button, Checkbox, FormGroup, FormControlLabel, CircularProgress, Alert, Stack,
  IconButton, Select, MenuItem, FormControl, InputLabel, FormHelperText} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { UserContext, UserData } from '../UserAndProfile/UserContext'; // Adjust path as needed
import { generateStudyPlan, StudyPlanConfig } from './studyPlanUtils'; // Adjust path as needed
// Assuming you have date-fns adapter installed: npm install @mui/x-date-pickers date-fns
// Make sure LocalizationProvider is wrapping your app root
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO, isValid, isFuture } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import theme from '@/muiTheme';

interface CreateStudyPlanModalProps {
  open: boolean;
  onClose: () => void;
}

const preferredTimeOptions = [
  "Mornings before school",
  "Commuting / Waiting for the bus",
  "Free time in school",
  "After school (before sports / activities)",
  "Right after getting home",
  "Evenings",
  "Weekends",
  "Other (flexible)",
];

const CreateStudyPlanModal: React.FC<CreateStudyPlanModalProps> = ({ open, onClose }) => {
  const { user, updateUserData, loading: userLoading, initialLoading } = useContext(UserContext);
  const navigate = useNavigate();

  // Form State
  const [targetMath, setTargetMath] = useState<number | ''>('');
  const [targetRw, setTargetRw] = useState<number | ''>('');
  const [testDate, setTestDate] = useState<Date | null>(null);
  const [sessionsPerWeek, setSessionsPerWeek] = useState<number | ''>('');
  const [minutesPerSession, setMinutesPerSession] = useState<number | ''>('');
  const [preferredTimes, setPreferredTimes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDiagnosticWarning, setShowDiagnosticWarning] = useState(false);

  // Derived state based on user context
  const needsTargetScoreInput = !user?.targetScores?.math || !user?.targetScores?.readingWriting;
  const needsTestDateInput = !user?.testDate;
  const isDiagnosticCompleted = !!user?.testAttempts?.filter(t => t.testType === 'diagnostic' && t.completed).length;

  // Effect to pre-populate form when modal opens or user data changes
  useEffect(() => {
    if (open && user) {
      setTargetMath(user.targetScores?.math ? parseInt(user.targetScores.math, 10) : 650);
      setTargetRw(user.targetScores?.readingWriting ? parseInt(user.targetScores.readingWriting, 10) : 650);
      
      const sixtyDaysFromNow = new Date();
      sixtyDaysFromNow.setDate(sixtyDaysFromNow.getDate() + 60);
      const initialDate = user.testDate ? parseISO(user.testDate) : sixtyDaysFromNow;
      setTestDate(isValid(initialDate) ? initialDate : sixtyDaysFromNow);
      
      setSessionsPerWeek(user.studyPlan?.weeklyPlans?.[0]?.weeklySessionGoal?.target || 3);
      // Assuming minutesPerSession was intended to be sourced from config if plan exists, else default
      // However, studyPlan object doesn't store minutesPerSession directly at the top level from config once generated.
      // For now, we'll use a common default or a value if you decide to store it in UserData or StudyPlanData.
      // Let's default to 15, or if user.studyPlan exists, perhaps we can infer from a task duration if needed, but that's complex.
      // Sticking to a simple default or a potential future field in user.studyPlan for average session length.
      setMinutesPerSession(15); // Defaulting, consider if this should come from existing plan's config if possible.

      setPreferredTimes(user.studyPlan?.userPreferredTimes || []);
      setError(null);
      setIsGenerating(false);
      setShowDiagnosticWarning(!isDiagnosticCompleted);
    }
  }, [open, user, isDiagnosticCompleted]);

  const handlePreferredTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    setPreferredTimes((prev) =>
      checked ? [...prev, value] : prev.filter((time) => time !== value)
    );
  };

  // Helper to check if a score input is valid
  const isScoreValid = (score: number | '') => {
    const numScore = Number(score); // Convert '' to 0, keep numbers as numbers
    return score !== '' && !isNaN(numScore) && numScore >= 200 && numScore <= 800;
  };

  // --- EDIT 1: Update onChange handlers for scores ---
  const handleScoreChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: React.Dispatch<React.SetStateAction<number | ''>>
   ) => {
    const value = event.target.value;
    if (value === '') {
      setter('');
    } else {
      const numValue = parseInt(value, 10);
      // Only set if it's a valid integer representation
      if (!isNaN(numValue)) {
         // Optionally add range clamping here if desired, though validation handles final check
         // setter(Math.max(200, Math.min(800, numValue)));
         setter(numValue);
      }
      // If it's not a valid integer (e.g., "abc"), do nothing or keep previous state
      // Depending on desired UX, you might want different behavior here.
      // For now, we only update state if it parses correctly.
    }
  };
  // --- END EDIT 1 ---

  const validateForm = (): boolean => {
    setError(null);
    setShowDiagnosticWarning(!isDiagnosticCompleted); // Show warning if diagnostic not done

    if (needsTargetScoreInput && (!isScoreValid(targetMath) || !isScoreValid(targetRw))) {
      setError("Please enter valid target scores between 200 and 800.");
      return false;
    }
    if (needsTestDateInput && (!testDate || !isValid(testDate) || !isFuture(testDate))) {
      setError("Please select a valid future test date.");
      return false;
    }
    // Convert to number for validation checks
    const numSessions = Number(sessionsPerWeek);
    if (sessionsPerWeek === '' || isNaN(numSessions) || numSessions <= 0 || numSessions > 21) {
      setError("Please enter a valid number of sessions per week (1-21).");
      return false;
    }
    const numMinutes = Number(minutesPerSession);
    if (minutesPerSession === '' || isNaN(numMinutes) || numMinutes <= 0 || numMinutes > 300) {
      setError("Please enter a valid duration per session (1-300 minutes).");
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm()) {
        // validateForm sets error and showDiagnosticWarning
        return;
    }
    setError(null); // Clear previous functional errors if validation passes

    if (!user) {
      setError("User data not available. Please try again later.");
      return;
    }

    const finalTargetMath = typeof targetMath === 'number' ? targetMath : (user.targetScores?.math ? parseInt(user.targetScores.math, 10) : 650);
    const finalTargetRw = typeof targetRw === 'number' ? targetRw : (user.targetScores?.readingWriting ? parseInt(user.targetScores.readingWriting, 10) : 650);
    const finalSessions = Number(sessionsPerWeek);
    const finalMinutes = Number(minutesPerSession);
    
    const sixtyDaysFromNow = new Date();
    sixtyDaysFromNow.setDate(sixtyDaysFromNow.getDate() + 60);
    const dateToUse = testDate || (user.studyPlan?.testDate ? parseISO(user.studyPlan.testDate) : sixtyDaysFromNow) ;
    const finalTestDate = (dateToUse && isValid(dateToUse)) ? dateToUse.toISOString().split('T')[0] : sixtyDaysFromNow.toISOString().split('T')[0];

    setIsGenerating(true);

    try {
      const config: StudyPlanConfig = { // Explicitly type config
        testDate: finalTestDate,
        targetScoreMath: finalTargetMath,
        targetScoreRw: finalTargetRw,
        sessionsPerWeek: finalSessions,
        minutesPerSession: finalMinutes,
        userPreferredTimes: preferredTimes,
        userId: user.uid
      };

      console.log("Generating study plan with config:", config, "Diagnostic completed: ", isDiagnosticCompleted);
      const newStudyPlan = await generateStudyPlan(config, user.skills, user.testAttempts);
      console.log("Generated study plan:", newStudyPlan);

      if (!newStudyPlan) {
          throw new Error("Study plan generation failed. Please check inputs.");
      }

      await updateUserData({ 
          studyPlan: newStudyPlan,
          // Persist target scores and test date if they were newly set in this modal
          ...(needsTargetScoreInput && { targetScores: { math: finalTargetMath.toString(), readingWriting: finalTargetRw.toString()} }),
          ...(needsTestDateInput && { testDate: finalTestDate })
      });
      onClose();
      navigate('/sat');

    } catch (err: any) {
      console.error("Error generating or saving study plan:", err);
      setError(err.message || "Failed to generate study plan. Please check your inputs and try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '700px',
    bgcolor: 'background.paper',
    border: '1px solid #ccc',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto',
    borderRadius: 2,
  };

  // Calculate error states for fields based on the main error state and validation logic
  const mathScoreError = !!error && needsTargetScoreInput && !isScoreValid(targetMath);
  const rwScoreError = !!error && needsTargetScoreInput && !isScoreValid(targetRw);
  const dateError = !!error && needsTestDateInput && (!testDate || !isValid(testDate) || !isFuture(testDate));
  const sessionsError = !!error && (sessionsPerWeek === '' || Number(sessionsPerWeek) <= 0 || Number(sessionsPerWeek) > 10);
  const minutesError = !!error && (minutesPerSession === '' || Number(minutesPerSession) <= 0 || Number(minutesPerSession) > 120);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="create-study-plan-modal-title"
      aria-describedby="create-study-plan-modal-description"
    >
      <Box sx={modalStyle}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
        <Typography id="create-study-plan-modal-title" variant="h5" component="h2" gutterBottom>
          {user?.studyPlan ? 'Update Your Study Plan' : 'Create Your Study Plan'}
        </Typography>

        {userLoading && !initialLoading ? (
          <CircularProgress />
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {showDiagnosticWarning && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Create your initial plan below, then it will be more personalized after you complete the diagnostic test.
                </Alert>
            )}
            <Stack spacing={3} mt={showDiagnosticWarning ? 0 : 2}>
              {/* Conditional Inputs */}
              {needsTargetScoreInput && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Target Math Score"
                    type="number"
                    value={targetMath}
                    onChange={(e) => handleScoreChange(e, setTargetMath)}
                    required
                    fullWidth
                    inputProps={{ min: 200, max: 800 }}
                    error={mathScoreError}
                    helperText={mathScoreError ? "Score must be between 200-800" : ""}
                  />
                  <TextField
                    label="Target R&W Score"
                    type="number"
                    value={targetRw}
                    onChange={(e) => handleScoreChange(e, setTargetRw)}
                    required
                    fullWidth
                    inputProps={{ min: 200, max: 800 }}
                    error={rwScoreError}
                    helperText={rwScoreError ? "Score must be between 200-800" : ""}
                  />
                </Stack>
              )}
              {needsTestDateInput && (
                 <DatePicker
                    label="Official Test Date"
                    value={testDate}
                    onChange={(newValue) => setTestDate(newValue)}
                    disablePast
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            required: true,
                            error: dateError,
                            helperText: dateError ? "Select a valid future date" : ""
                        }
                    }}
                 />
              )}

              {/* Time Commitment Inputs */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth required error={sessionsError}>
                   <InputLabel id="sessions-per-week-label">Sessions Per Week</InputLabel>
                   <Select
                     labelId="sessions-per-week-label"
                     id="sessions-per-week-select"
                     value={sessionsPerWeek}
                     label="Sessions Per Week"
                     onChange={(e) => setSessionsPerWeek(e.target.value === '' ? '' : Number(e.target.value))}
                   >
                     {[...Array(10)].map((_, i) => (
                       <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                     ))}
                   </Select>
                   {sessionsError && <FormHelperText>Select a valid number (1-10)</FormHelperText>}
                 </FormControl>

                <FormControl fullWidth required error={minutesError}>
                  <InputLabel id="minutes-per-session-label">Minutes Per Session</InputLabel>
                  <Select
                    labelId="minutes-per-session-label"
                    id="minutes-per-session-select"
                    value={minutesPerSession}
                    label="Minutes Per Session"
                    onChange={(e) => setMinutesPerSession(e.target.value === '' ? '' : Number(e.target.value))}
                  >
                    {[5, 10, 15, 30, 45, 60, 90].map((mins) => (
                      <MenuItem key={mins} value={mins}>{mins}</MenuItem>
                    ))}
                  </Select>
                  {minutesError && <FormHelperText>Select a valid duration (5-90)</FormHelperText>}
                </FormControl>
              </Stack>

              {/* Preferred Times */}
              <FormControl component="fieldset" variant="standard">
                <Typography variant="h6" gutterBottom>When do you plan to study?</Typography> 
                <Typography variant="subtitle1" gutterBottom sx={{color: 'text.primary'}}>
                  Try to get{' '}
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    3-4 times a week
                  </Box>
                  , and learn {' '}
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    1 new thing {' '}
                  </Box>
                  each time with reflection and asking the AI. <br />
                  <span style={{fontStyle: 'italic'}}>You got this! And remember: You can always use your phone!</span>
                </Typography>
                <FormGroup>
                  {preferredTimeOptions.map((time) => (
                    <FormControlLabel
                      key={time}
                      control={
                        <Checkbox
                          checked={preferredTimes.includes(time)}
                          onChange={handlePreferredTimeChange}
                          value={time}
                        />
                      }
                      label={time}
                    />
                  ))}
                </FormGroup>
              </FormControl>

              {/* General Error Display */}
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                disabled={isGenerating || (userLoading && initialLoading)} // Only truly disabled during initial full load or generation
                fullWidth
                sx={{ mt: 2 }}
              >
                {isGenerating ? <CircularProgress size={24} /> : (user?.studyPlan ? 'Update My Plan' : 'Create My Plan')}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default CreateStudyPlanModal;